/**
 * NutritionPage - Admin overview of customer nutrition data
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Apple, Search, Users, Target, TrendingUp, Calendar,
  ChevronRight, Droplets, Flame, X, Flag, AlertTriangle, Settings, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import CustomerNutrition from '@/components/CustomerNutrition';
import NutritionGoalsEditor from '@/components/nutrition/NutritionGoalsEditor';
import NutritionOnboarding from '@/components/nutrition/NutritionOnboarding';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const NutritionPage = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [pendingReviews, setPendingReviews] = useState(0);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [customerOnboardingData, setCustomerOnboardingData] = useState(null);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    activeTrackers: 0,
    averageCalories: 0,
    averageWater: 0
  });

  useEffect(() => {
    loadCustomers();
    loadStats();
    loadPendingReviews();
  }, []);

  const loadPendingReviews = async () => {
    try {
      const response = await axios.get(`${API}/portal/nutrition/admin/product-reviews/stats`);
      setPendingReviews(response.data.pending || 0);
    } catch (error) {
      console.log('Reviews stats not available');
    }
  };

  // Load customer body data for onboarding wizard
  const loadCustomerOnboardingData = async (customerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/nutrition/customer/${customerId}/onboarding-data`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomerOnboardingData(response.data);
      return response.data;
    } catch (error) {
      console.log('Could not load onboarding data:', error);
      setCustomerOnboardingData(null);
      return null;
    }
  };

  // Open onboarding wizard with customer data
  const handleOpenOnboardingWizard = async () => {
    if (selectedCustomer) {
      await loadCustomerOnboardingData(selectedCustomer.id);
      setShowOnboardingWizard(true);
    }
  };

  const loadCustomers = async () => {
    try {
      // Get all customers
      const response = await axios.get(`${API}/customers`);
      const allCustomers = response.data;
      
      // Get nutrition data for each customer (last 7 days)
      const customersWithNutrition = await Promise.all(
        allCustomers.map(async (customer) => {
          try {
            const nutritionResponse = await axios.get(
              `${API}/nutrition/customer/${customer.id}/entries`,
              { params: { days: 7 } }
            );
            const entries = nutritionResponse.data;
            
            // Calculate summary
            const totalCalories = entries.reduce((sum, e) => sum + (e.calories || 0), 0);
            const avgCalories = entries.length > 0 ? Math.round(totalCalories / 7) : 0;
            
            return {
              ...customer,
              hasNutritionData: entries.length > 0,
              recentEntries: entries.length,
              avgDailyCalories: avgCalories,
              lastEntry: entries[0]?.date || null
            };
          } catch {
            return {
              ...customer,
              hasNutritionData: false,
              recentEntries: 0,
              avgDailyCalories: 0,
              lastEntry: null
            };
          }
        })
      );
      
      // Sort: customers with nutrition data first
      customersWithNutrition.sort((a, b) => {
        if (a.hasNutritionData && !b.hasNutritionData) return -1;
        if (!a.hasNutritionData && b.hasNutritionData) return 1;
        return 0;
      });
      
      setCustomers(customersWithNutrition);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/nutrition/admin/stats`);
      setStats(response.data);
    } catch (error) {
      // Stats endpoint might not exist, use defaults
      console.log('Stats not available');
    }
  };

  const filteredCustomers = customers.filter(customer => {
    if (!searchQuery) return true;
    const fullName = `${customer.first_name || ''} ${customer.last_name || ''}`.toLowerCase();
    const email = (customer.email || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    return fullName.includes(query) || email.includes(query);
  });

  const customersWithData = filteredCustomers.filter(c => c.hasNutritionData);
  const customersWithoutData = filteredCustomers.filter(c => !c.hasNutritionData);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Apple className="h-8 w-8 text-green-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ernährung</h1>
            <p className="text-gray-500">Übersicht der Kundenernährung</p>
          </div>
        </div>
        
        {/* Product Reviews Button */}
        <Button
          variant="outline"
          onClick={() => navigate('/product-reviews')}
          className={`flex items-center gap-2 ${pendingReviews > 0 ? 'border-orange-500 text-orange-600' : ''}`}
        >
          <Flag className="h-4 w-4" />
          Nährwert-Reviews
          {pendingReviews > 0 && (
            <Badge className="bg-orange-500 text-white ml-1">{pendingReviews}</Badge>
          )}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Kunden gesamt</p>
                <p className="text-2xl font-bold">{customers.length}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Aktive Tracker</p>
                <p className="text-2xl font-bold text-green-600">
                  {customers.filter(c => c.hasNutritionData).length}
                </p>
              </div>
              <Target className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Ø Kalorien/Tag</p>
                <p className="text-2xl font-bold text-orange-600">
                  {stats.averageCalories || '—'}
                </p>
              </div>
              <Flame className="h-8 w-8 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Ø Wasser/Tag</p>
                <p className="text-2xl font-bold text-blue-600">
                  {stats.averageWater ? `${(stats.averageWater / 1000).toFixed(1)}L` : '—'}
                </p>
              </div>
              <Droplets className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input
          placeholder="Kunde suchen..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Customers with Nutrition Data */}
      {customersWithData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-500" />
              Aktive Ernährungstracker ({customersWithData.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {customersWithData.map(customer => (
                <div
                  key={customer.id}
                  className="flex items-center justify-between p-3 bg-green-50 rounded-lg hover:bg-green-100 cursor-pointer transition-colors"
                  onClick={() => setSelectedCustomer(customer)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-green-200 flex items-center justify-center text-green-700 font-semibold">
                      {(customer.first_name?.[0] || customer.email?.[0] || '?').toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium">
                        {customer.first_name} {customer.last_name}
                      </p>
                      <p className="text-sm text-gray-500">{customer.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium text-green-700">
                        {customer.avgDailyCalories} kcal/Tag
                      </p>
                      <p className="text-xs text-gray-500">
                        {customer.recentEntries} Einträge (7 Tage)
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Customers without Nutrition Data */}
      {customersWithoutData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-gray-400" />
              Ohne Ernährungsdaten ({customersWithoutData.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {customersWithoutData.slice(0, 10).map(customer => (
                <div
                  key={customer.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  onClick={() => setSelectedCustomer(customer)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-semibold">
                      {(customer.first_name?.[0] || customer.email?.[0] || '?').toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium">
                        {customer.first_name} {customer.last_name}
                      </p>
                      <p className="text-sm text-gray-500">{customer.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant="outline" className="text-gray-500">
                      Keine Daten
                    </Badge>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              ))}
              {customersWithoutData.length > 10 && (
                <p className="text-center text-sm text-gray-500 py-2">
                  + {customersWithoutData.length - 10} weitere Kunden
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Customer Nutrition Dialog */}
      <Dialog open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Apple className="h-5 w-5 text-green-500" />
              Ernährung: {selectedCustomer?.first_name} {selectedCustomer?.last_name}
            </DialogTitle>
          </DialogHeader>
          {selectedCustomer && (
            <Tabs defaultValue="goals" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="goals" className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Ziele
                </TabsTrigger>
                <TabsTrigger value="entries" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Einträge
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="goals">
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">🎯</span>
                      <div>
                        <h3 className="font-semibold text-lg">Ernährungsziele</h3>
                        <p className="text-sm text-gray-500">Kalorien, Makros & Mahlzeiten-Verteilung</p>
                      </div>
                    </div>
                    {/* Automatic Setup Button */}
                    <Button
                      onClick={handleOpenOnboardingWizard}
                      className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black gap-2"
                    >
                      <Sparkles className="w-4 h-4" />
                      Automatisch berechnen
                    </Button>
                  </div>
                  
                  {/* Manual Entry */}
                  <NutritionGoalsEditor
                    customerId={selectedCustomer.id}
                    token={localStorage.getItem('token')}
                    apiUrl={API}
                    isAdmin={true}
                    compact={false}
                    onSave={() => {
                      toast.success('Ziele gespeichert');
                    }}
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="entries">
                <CustomerNutrition 
                  customerId={selectedCustomer.id} 
                  customerName={`${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`}
                />
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Onboarding Wizard for Admin - Now uses same 8-step wizard as customer portal */}
      {selectedCustomer && (
        <NutritionOnboarding
          open={showOnboardingWizard}
          onClose={() => {
            setShowOnboardingWizard(false);
            setCustomerOnboardingData(null);
          }}
          onComplete={() => {
            toast.success('Ernährungsziele erfolgreich gespeichert!');
            setShowOnboardingWizard(false);
            setCustomerOnboardingData(null);
            // Reload customer data
            if (selectedCustomer) {
              loadCustomers();
            }
          }}
          token={localStorage.getItem('token')}
          customerId={selectedCustomer.id}
          isAdmin={true}
          existingData={customerOnboardingData}
        />
      )}
    </div>
  );
};

export default NutritionPage;
